import React, { Component, useState, useEffect, useRef } from 'react'
import railsMethodLink from '../utils/rails_method_link'
import { Turnstile } from '@marsidev/react-turnstile'

const notificationGranted = () => {
  return localStorageGet('sngNotify') === 'true' && Notification.permission === 'granted'
}

function renderCancelButton (data, cancel) {
  if (!data.user_is_in_tournament || data.current_tournament_status !== 'waiting') { return }

  return (
    <button
      type='button'
      disabled={data.loading}
      className='button-1 big red'
      onClick={cancel}>
      Cancel Participation
    </button>
  )
}

function renderGameStartingMsg (data) {
  if (!data.user_is_in_tournament || data.current_tournament_status !== 'starting' || data.room_url) { return }

  return (
    <a href='#' className='button-1 big green disabled' disabled>
      Game starting, wait please…
    </a>
  )
}

const useRenderCounter = () => {
  const counter = useRef(0)

  counter.current++

  return counter.current
}

const MemoizedRoomButton = React.memo(function RoomButton (props) {
  const renderCount = useRenderCounter()

  if (!props.room_url) { return null }

  if (renderCount !== 1 && notificationGranted()) {
    const notification =
      new Notification(
        'Your Poker Now Sit & Go Game is Ready!',
        { body: 'Click here to go to your Game!' }
      )

    notification.onclick = function(event) {
      event.preventDefault()
      window.open(props.room_url, '_blank')
    }
  }

  return (
    <React.Fragment>
      <a href={props.room_url} target='_blank' className='button-1 big green'>
        Click here to go to the Room
      </a>
    </React.Fragment>
  )
}, (prevProps, nextProps) => prevProps.room_url === nextProps.room_url)

function renderFinishedTournamentMsg (data) {
  if (!data.user_is_in_tournament || data.current_tournament_status !== 'finished') { return }

  return (
    <p>This tournament is finished!</p>
  )
}

function renderJoinButton (props) {
  const [captchaResponse, setCaptchaResponse] = useState(null)

  const { data, join } = props

  if (data.user_is_in_tournament || data.current_tournament_status !== 'waiting') { return }

  let captcha

  if (data.needs_captcha) {
    captcha = (
      <div>
        <p>
          Please solve the captcha in order to join in the tournament.
        </p>

        <Turnstile
          siteKey={window.TURNSTILE_SITE_KEY}
          onSuccess={(response) => setCaptchaResponse(response)} />
      </div>
    )
  }

  return (
    <div>
      <button
        type='button'
        disabled={data.loading || (data.needs_captcha && !captchaResponse)}
        className='button-1 big blue'
        onClick={() => join(captchaResponse)}>
        Join on this Tournament
      </button>

      {captcha}
    </div>
  )
}

function localStorageSet (key, value) {
  try {
    window.localStorage.setItem(key, value)
  } catch (e) {
    console.log(`Not possible to write "${key}" on local storage! Error: ${e.message}`)
  }
}

function localStorageGet (key, value) {
  try {
    return window.localStorage.getItem(key, value)
  } catch (e) {
    console.log(`Not possible to get "${key}" on local storage! Error: ${e.message}`)
    return null
  }
}

function NotificationGrant (props) {
  const [notify, setNotify] = useState(notificationGranted())

  const togglePermission = () => {
    if (notificationGranted()) {
      localStorageSet('sngNotify', 'false')
      setNotify(false)
    } else {
      if (Notification.permission !== 'granted') {
        Notification.requestPermission().then(function (permission) {
          if (permission === 'granted') {
            localStorageSet('sngNotify', 'true')
            setNotify(true)
          }
        })
      } else {
        localStorageSet('sngNotify', 'true')
        setNotify(true)
      }
    }
  }

  return (
    <React.Fragment>
      <label style={{ display: 'block' }}>
        <br />
        <input type='checkbox' onClick={togglePermission} readOnly checked={notify} />
        &nbsp;
        Notify me when the Game is Ready to Play
      </label>
    </React.Fragment>
  )
}

export default function TournamentApp (props) {
  const { data } = props

  if (!Object.keys(data).length) {
    return null
  }

  if (data.paused) {
    return (
      <div>
        <h1 className='title-2 with-bottom-line'>
          Paused for Maintenance
        </h1>

        <div className='tournament-players-container'>
          <p className='sub'>
            {data.paused}
          </p>
        </div>
      </div>
    )
  }

  let onlinePlayersDots = []

  for (let i = 1; i <= data.seats_available; i++) {
    let isEmpty = (i > data.registered_players_quantity)

    onlinePlayersDots.push(
      <div
        key={`tournament-dot-${i}`}
        className={`tournament-player ${isEmpty ? 'hiding' : ''}`}>
        Player
      </div>
    )
  }

  let startingAtMessage

  if (data.will_starts_at && !data.room_url) {
    startingAtMessage = (
      <p>Starting at {data.will_starts_at}</p>
    )
  }

  let blindStructure = [20, 40, 60, 100, 150, 200, 300, 500]

  return (
    <div>
      {!data.connected && <div className='internal-warning-1 red'>
        You are disconnected from the server. <a href='#' onClick={(evt) => { evt.preventDefault(); window.location.reload() }}>Click here</a> to reconnect.
      </div>}

      <div className='tournament-players-container'>
        <div className='tournament-players'>
          {onlinePlayersDots}
        </div>

        <p className='sub'>
          {data.registered_players_quantity} players registered of {data.seats_available} max (starts with 4)
        </p>

        {startingAtMessage}
      </div>

      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        {renderCancelButton(data, props.cancel)}
        {renderGameStartingMsg(data)}
        {<MemoizedRoomButton room_url={data.room_url} />}
        {renderFinishedTournamentMsg(data)}
        {renderJoinButton(props)}
        {data.user_is_logged && <NotificationGrant />}
      </div>

      <div className='infos-box-1'>
        <div className='info-box'>
          <p>Texas Hold'em</p>
        </div>

        <div className='info-box'>
          <p>{data.tournament_initial_stack} chips initial stack</p>
        </div>

        <div className='info-box'>
          <p>Blinds starting at {data.tournament_big_blind_value / 2}/{data.tournament_big_blind_value}</p>
          <p className='sub'>Blinds increasing every {data.tournament_blind_minutes_increase_time} minutes</p>
          <p className='sub'>Blind structure {blindStructure.map(blind => `${blind/2}/${blind}`).join(', ')}, (300/600 in > 6 seats table)</p>
        </div>

        <div className='info-box'>
          <p>Earn season rank points</p>
        </div>
      </div>
    </div>
  )
}
